namespace Solovis.Portal {

    export interface IPortalServices {

        siteUrl(): string;

        /**
         * If this context represents a proxied environment, rather than being part of the native site, this will return true.
         */
        isProxiedDataEnvironment(): boolean;

        /**
         * If this context represents a proxied environment, rather than being part of the native site, this will return the actual proxied data environmnet id.
         */
        proxiedDataEnvironmentId(): number | null;

        rpc(): Solovis.RpcClients.IRpcClient;

        reportDataLoader(): Solovis.Portal.IReportDataLoader;

        modelEnvironmentCachedData(): Solovis.Portal.IModelEnvironmentCachedData;

        reportLinker(): Solovis.Portal.IReportLinker;

        /**
         * The Custom data typically available at Solovis.Custom in a client data environment for a given user. Never edit this. Treat as read-only.
         */
        customEnvironmentUserData(): any;

        cachedPermissions(): ICachedPermissions;

        cachedModuleOptions(): ICachedModuleOptions;
    }


    /**
     * Use this when code is running in the actual data environment.
     */
    export class SitePortalServices implements IPortalServices {

        constructor() {
        }

        public siteUrl(): string {
            return (Solovis as any).Info.siteRoot;
        }

        public isProxiedDataEnvironment(): boolean {
            return false;
        }

        public proxiedDataEnvironmentId() {
            return null;
        }

        public rpc(): Solovis.RpcClients.IRpcClient {
            return Solovis.Rpc;
        }

        public reportDataLoader(): Solovis.Portal.IReportDataLoader {
            return (Solovis as any).Reports.ReportDataLoader;
        }

        public modelEnvironmentCachedData(): Solovis.Portal.IModelEnvironmentCachedData {
            return (Solovis as any).Reports.LocalCache.ModelEnvironmentCachedData;
        }

        public reportLinker(): Solovis.Portal.IReportLinker {
            return (Solovis as any).Reports.ReportLinker;
        }

        public customEnvironmentUserData(): any {
            return (Solovis as any).Custom;
        }

        public cachedPermissions(): Solovis.Portal.ICachedPermissions {
            return Solovis.Portal.CachedPermissions;
        }

        public cachedModuleOptions(): ICachedModuleOptions {
            return Solovis.Portal.CachedModuleOptions;
        }       
    }





    export interface ProxiedServiceOverrides {
        rpc?: () => Solovis.RpcClients.IRpcClient;
        reportDataLoader?: () => Solovis.Portal.IReportDataLoader;
        modelEnvironmentCachedData?: () => Solovis.Portal.IModelEnvironmentCachedData;
        reportLinker?: () => Solovis.Portal.IReportLinker;
        cachedPermissions?: () => Solovis.Portal.ICachedPermissions;
        cachedModuleOptions?: () => Solovis.Portal.ICachedModuleOptions;
    }


    /**
     * Use this when code is running in a hub-style environment and needs to use existing controls to target a dependent data environment.
     */
    export class ProxiedPortalServices implements IPortalServices {

        private _dataEnvironmentId: number;
        private _siteUrl: string;
        private _customEnvironmentUserData: any;

        private _serviceOverrides: ProxiedServiceOverrides;

        private _rpc: Solovis.RpcClients.IRpcClient;
        private _reportLinker: Solovis.Portal.IReportLinker;
        private _reportDataLoader: Solovis.Portal.IReportDataLoader;
        private _modelEnvironmentCachedData: Solovis.Portal.IModelEnvironmentCachedData;
        private _cachedPermissions: Solovis.Portal.ICachedPermissions;
        private _cachedModuleOptions: Solovis.Portal.ICachedModuleOptions;

        constructor(options: {
            dataEnvironmentId: number;
            siteUrl: string;
            serviceOverrides?: ProxiedServiceOverrides;
            customEnvironmentUserData?: any;
        }) {
            // NOTE: Use createAsync as that will eventually grab extra options from server.

            this._dataEnvironmentId = options.dataEnvironmentId;
            this._siteUrl = options.siteUrl;
            this._customEnvironmentUserData = options.customEnvironmentUserData;

            this._serviceOverrides = options.serviceOverrides;
        }

        public siteUrl(): string {
            if (this._siteUrl) {
                return this._siteUrl;
            }
            else {
                throw "siteUrl not available.";
            }
        }

        public isProxiedDataEnvironment(): boolean {
            return true;
        }

        public proxiedDataEnvironmentId() {
            return this._dataEnvironmentId;
        }

        public rpc(): Solovis.RpcClients.IRpcClient {
            if (!this._rpc) {
                if (this._serviceOverrides && this._serviceOverrides.rpc) {
                    this._rpc = this._serviceOverrides.rpc();
                }
                else {
                    this._rpc = new Solovis.RpcClients.DataEnvProxyRpcClient({ dataEnvironmentId: this._dataEnvironmentId });
                }
            }

            return this._rpc;
        }

        public reportDataLoader(): Solovis.Portal.IReportDataLoader {
            if (!this._reportDataLoader) {
                if (this._serviceOverrides && this._serviceOverrides.reportDataLoader) {
                    this._reportDataLoader = this._serviceOverrides.reportDataLoader();
                }
                else {
                    this._reportDataLoader = new (Solovis as any).Reports.ReportDataLoaderClient({
                        portalServices: this
                    });
                }
            }

            return this._reportDataLoader;
        }

        public modelEnvironmentCachedData(): Solovis.Portal.IModelEnvironmentCachedData {
            if (!this._modelEnvironmentCachedData) {
                if (this._serviceOverrides && this._serviceOverrides.modelEnvironmentCachedData) {
                    this._modelEnvironmentCachedData = this._serviceOverrides.modelEnvironmentCachedData();
                }
                else {
                    this._modelEnvironmentCachedData = new (Solovis as any).Reports.LocalCache.ModelEnvironmentCachedDataClient({
                        portalServices: this
                    });
                }
            }

            return this._modelEnvironmentCachedData;
        }

        public reportLinker(): Solovis.Portal.IReportLinker {
            if (!this._reportLinker) {
                if (this._serviceOverrides && this._serviceOverrides.reportDataLoader) {
                    this._reportLinker = this._serviceOverrides.reportLinker();
                }
                else {
                    this._reportLinker = new (Solovis as any).Reports.ReportLinkerClient(this);
                }
            }

            return this._reportLinker;
        }

        public customEnvironmentUserData(): any {
            return this._customEnvironmentUserData;
        }

        public cachedPermissions(): Solovis.Portal.ICachedPermissions {
            if (!this._cachedPermissions) {
                if (this._serviceOverrides && this._serviceOverrides.cachedPermissions) {
                    this._cachedPermissions = this._serviceOverrides.cachedPermissions();
                }
                else {
                    this._cachedPermissions = new Solovis.Portal.CachedPermissionsClient(this);
                }
            }

            return this._cachedPermissions;
        }

        public cachedModuleOptions(): ICachedModuleOptions {
            if (!this._cachedModuleOptions) {
                if (this._serviceOverrides && this._serviceOverrides.cachedModuleOptions) {
                    this._cachedModuleOptions = this._serviceOverrides.cachedModuleOptions();
                }
                else {
                    this._cachedModuleOptions = new Solovis.Portal.CachedModuleOptionsClient(this);
                }
            }

            return this._cachedModuleOptions;
        }


        public static async createAsync(options: {
            dataEnvironmentId: number,
            serviceOverrides?: ProxiedServiceOverrides
        }): Promise<ProxiedPortalServices> {

            let response = await Solovis.Rpc.invoke("/Rpc/DataEnvProxy/GetDataEnvironmentProxyContext", { dataEnvironmentId: options.dataEnvironmentId });
            let result = response.result;

            return new ProxiedPortalServices({
                dataEnvironmentId: options.dataEnvironmentId,
                serviceOverrides: options.serviceOverrides,
                siteUrl: result.siteUrl,
                customEnvironmentUserData: result.custom
            });
        }
    }
}